import { SeverityLevel } from '@microsoft/applicationinsights-web';
import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { safeTrackException } from '../appInsights';
import { getCurrentLanguage } from '../i18n/i18nConfig';
import { showGlobalSnackbar } from '../providers/SnackbarProvider';
import {
  acquireAccessToken,
  forceTokenRefresh,
  getActiveAccount,
  redirectToLogin,
  requiresInteraction,
} from './AuthToken';
import { ErrorModel } from './models/ErrorModel';
import { isPreviewArmed } from '../views/try/mock/previewMode';

const API_URL = `${(window as any).app.env.API_URL}/api`;

export interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  showSnackbar?: boolean;
}

/**
 * Marks a request that never reached the network because there was no usable token. It is an
 * expected step of the re-auth flow rather than a fault, so it is reported to nobody.
 */
export const ERR_AUTH_REQUIRED = 'ERR_AUTH_REQUIRED';

type TrackedRequestConfig = InternalAxiosRequestConfig & {
  // Set once a request has been replayed with a freshly forced token, so a second 401 on the same
  // request falls through to the normal error path instead of refreshing and retrying forever.
  authRetried?: boolean;
  // The dedupe key and controller are stamped on the config at registration because they cannot be
  // recomputed afterwards: `config.data` is the caller's object on the way in and a serialized
  // string on the way out, so a `requestKey` carried in the body yields a different key each time.
  requestKey?: string;
  abortController?: AbortController;
};

function authRequiredError(message: string, config: InternalAxiosRequestConfig): Error {
  return Object.assign(new Error(message), { code: ERR_AUTH_REQUIRED, config });
}

function setAuthHeader(config: InternalAxiosRequestConfig, token: string | null): void {
  const value = token && `Bearer ${token}`;
  if (config.headers && typeof config.headers.set === 'function') {
    if (value) config.headers.set('Authorization', value);
    else config.headers.delete('Authorization');
  } else if (config.headers) {
    // Kept for the pre-AxiosHeaders config shape the preview dispatcher can still hand over.
    if (value) Object.assign(config.headers, { Authorization: value });
    else delete (config.headers as Record<string, unknown>).Authorization;
  }
}

const axiosClient = axios.create({
  paramsSerializer: {
    indexes: null,
  },
  baseURL: API_URL,
  withCredentials: true,
  showSnackbar: true,
} as ExtendedAxiosRequestConfig);
const abortControllers = new Map<string, AbortController>();

// --- Preview-sandbox adapter hook -------------------------------------------------------
// The "Try It Now" feature registers a fail-closed mock dispatcher here when its (lazy) module
// loads. Core deliberately does NOT import the feature — it only exposes this seam — so the
// dependency direction stays clean. When no dispatcher is registered, or it returns null
// (preview not armed), the real network adapter runs with zero behaviour change. When the
// dispatcher returns a promise (preview armed) the real adapter is never reached.
type PreviewDispatcher = (config: InternalAxiosRequestConfig) => Promise<AxiosResponse> | null;
let previewDispatcher: PreviewDispatcher | null = null;
export function registerPreviewDispatcher(fn: PreviewDispatcher | null): void {
  previewDispatcher = fn;
}

const realAdapter = axios.getAdapter(axiosClient.defaults.adapter || axios.defaults.adapter);
axiosClient.defaults.adapter = (config) => {
  const handled = previewDispatcher?.(config);
  return handled ?? realAdapter(config);
};

axiosClient.interceptors.request.use(async (config: TrackedRequestConfig) => {
  const requestKey =
    config.requestKey ?? (config?.data?.requestKey || `${config.method}:${config.url}`);
  config.requestKey = requestKey;

  const controller = new AbortController();
  // The first attempt's signal is spent by the time a replay runs, so every attempt needs its own.
  config.signal = controller.signal;

  // A replay is the same request finishing, not a new competitor for its key. Registering it would
  // abort whatever the app issued while the token was refreshing, and hand the stale answer the win.
  if (!config.authRetried) {
    // If there's a pending request with the same key, abort it
    abortControllers.get(requestKey)?.abort();
    abortControllers.set(requestKey, controller);
    config.abortController = controller;
  }

  // --- Accept-Language Header ---
  const currentLanguage = getCurrentLanguage();
  if (config.headers && typeof config.headers.set === 'function') {
    config.headers.set('Accept-Language', currentLanguage);
  } else if (config.headers) {
    Object.assign(config.headers, { 'Accept-Language': currentLanguage });
  }

  // --- MSAL Access Token Injection ---
  // Preview sandbox: the mock adapter answers every request locally, so skip the B2C token call
  // entirely (acquireTokenSilent/Redirect are real external round-trips that could bounce the
  // prospect to a login page mid-demo).
  if (!isPreviewArmed()) {
    // Clear first and set only on success. A stale token is worse than no token: the server
    // rejects it with a 401 on every endpoint it is replayed against, so one dead token fails
    // every open screen at once and fills the API log with expired-token validation failures.
    setAuthHeader(config, null);

    const account = getActiveAccount();
    if (account) {
      try {
        setAuthHeader(config, await acquireAccessToken(account));
      } catch {
        // The session is gone and no token is coming. Send the user back to B2C once, and fail
        // this request here rather than spending a round trip to be told the same thing.
        redirectToLogin(account);
        throw authRequiredError('Silent token refresh failed', config);
      }
    }
  }
  // --- End MSAL Access Token Injection ---

  return config;
});

function releaseController(config: TrackedRequestConfig | undefined): void {
  // Identity-checked: a replay, or a request whose key was taken over by a newer one, must not
  // evict the registration that is now governing that key.
  if (config?.requestKey && abortControllers.get(config.requestKey) === config.abortController) {
    abortControllers.delete(config.requestKey);
  }
}

axiosClient.interceptors.response.use(
  (response) => {
    releaseController(response.config as TrackedRequestConfig);
    return response;
  },
  async (error) => {
    const config = error.config as TrackedRequestConfig | undefined;

    // A 401 means the server rejected the very token MSAL had just handed over as valid - MSAL
    // judges expiry against the browser's clock, so skew and a mid-session key rollover both land
    // here. Force one new token and replay the request once; the alternative is that the user
    // watches every screen fail until they think to reload the page.
    if (error.response?.status === 401 && config && !config.authRetried && !isPreviewArmed()) {
      const account = getActiveAccount();
      if (account) {
        config.authRetried = true;
        let renewed = false;
        try {
          await forceTokenRefresh(account);
          renewed = true;
        } catch (refreshError) {
          // Not every 401 is about the token - some endpoints answer an authorisation failure with
          // one - and a refresh can fail for reasons that say nothing about the session. Signing a
          // valid session out over either would be a far worse bug than the one being fixed, so
          // only MSAL's own "the user must sign in again" is allowed to do it.
          if (requiresInteraction(refreshError)) {
            redirectToLogin(account);
            return Promise.reject(authRequiredError('Re-authentication required', config));
          }
        }
        // The request interceptor attaches the refreshed token. A second 401 is no longer about
        // token freshness, so it is left to fail normally rather than retried again.
        if (renewed) return axiosClient(config);
      }
    }

    // Preview sandbox: a fail-closed mock rejection (ERR_PREVIEW_UNHANDLED) is expected, not a
    // real error — don't log telemetry or pop a snackbar to the prospect.
    // Re-auth (ERR_AUTH_REQUIRED) is expected too: the user is already on their way to B2C.
    if (error.code !== 'ERR_CANCELED' && error.code !== ERR_AUTH_REQUIRED && !isPreviewArmed()) {
      const status = error.response?.status ?? 0;
      safeTrackException(
        error,
        {
          source: 'axios',
          status,
          method: error.config?.method,
          // Strip the query string - it can carry emails/search text (PII)
          urlPath: (error.config?.url ?? '').split('?')[0],
          errorCode: error.code,
          requestId: error.response?.headers?.['request-id'],
        },
        status >= 500 || status === 0 ? SeverityLevel.Error : SeverityLevel.Warning
      );
    }

    if (
      error.code !== 'ERR_CANCELED' &&
      error.code !== ERR_AUTH_REQUIRED &&
      error.status !== 409 &&
      error.config?.showSnackbar &&
      !isPreviewArmed()
    ) {
      const errorMessage = new ErrorModel(
        ErrorModel.Severity.FAIL,
        error,
        error?.response?.config?.url
      );
      showGlobalSnackbar(errorMessage.message, 'error');
    }

    // Preview sandbox: a blocked write (Create/Edit/Delete) is expected. Surface the friendly,
    // read-only message as a gentle info snackbar instead of letting the raw rejection bubble up.
    if (
      isPreviewArmed() &&
      error.code === 'ERR_PREVIEW_READONLY' &&
      error.config?.showSnackbar !== false
    ) {
      showGlobalSnackbar(error.message, 'info');
    }

    releaseController(config);

    return Promise.reject(error);
  }
);

export default axiosClient;
